// =============================================================================
// Lumen Viewer — axios client with MSAL bearer-token interceptor
// =============================================================================

import axios from 'axios';
import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalConfig, loginRequest } from '../auth/msalConfig.js';

// Reuse the single PublicClientApplication via a module-scope ref. main.jsx
// creates the instance; this module imports config and reads from the cached
// instance via getActiveAccount().
// NOTE: we can't import the instance directly without a circular dep, so we
// use the session cache to recover the account.
const msalInstance = new PublicClientApplication(msalConfig);

async function acquireToken() {
  const accounts = msalInstance.getAllAccounts();
  const account = accounts[0] || msalInstance.getActiveAccount();
  if (!account) throw new Error('no-account');

  try {
    const r = await msalInstance.acquireTokenSilent({ ...loginRequest, account });
    return r.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      await msalInstance.acquireTokenRedirect({ ...loginRequest, account });
    }
    throw err;
  }
}

const client = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  timeout: 60_000,
  headers: { 'Content-Type': 'application/json' },
});

client.interceptors.request.use(async (cfg) => {
  try {
    const tok = await acquireToken();
    if (tok) cfg.headers.Authorization = `Bearer ${tok}`;
  } catch { /* AuthGate will redirect */ }
  return cfg;
});

client.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      msalInstance.loginRedirect(loginRequest).catch(() => {});
    }
    return Promise.reject(err);
  }
);

// --- Typed-ish helpers --------------------------------------------------------
export async function listStudies(params = {}) {
  const { data } = await client.get('/studies', { params });
  return data;
}
export async function getStudy(uid) {
  const { data } = await client.get(`/study/${encodeURIComponent(uid)}`);
  return data;
}
export async function generateAiRead(uid) {
  const { data } = await client.post(`/study/${encodeURIComponent(uid)}/airead`);
  return data;
}
export async function signReport(uid, id, edits = {}) {
  const { data } = await client.put(`/study/${encodeURIComponent(uid)}/airead/${encodeURIComponent(id)}/sign`, edits);
  return data;
}

// Download a blob (.dcm) via its SAS URL. Returns ArrayBuffer.
export async function downloadDicom(sasUrl) {
  const r = await fetch(sasUrl);
  if (!r.ok) throw new Error(`downloadDicom HTTP ${r.status}`);
  return await r.arrayBuffer();
}

// --- Feature endpoints ------------------------------------------------------
export async function listMeasurements(uid) {
  const { data } = await client.get(`/study/${encodeURIComponent(uid)}/measurements`);
  return data.measurements || [];
}
export async function saveMeasurement(uid, payload) {
  const { data } = await client.post(`/study/${encodeURIComponent(uid)}/measurements`, payload);
  return data;
}
export async function deleteMeasurement(uid, id) {
  await client.delete(`/study/${encodeURIComponent(uid)}/measurements?id=${encodeURIComponent(id)}`);
}

export async function listAnnotations(uid) {
  const { data } = await client.get(`/study/${encodeURIComponent(uid)}/annotations`);
  return data.annotations || [];
}
export async function saveAnnotation(uid, payload) {
  const { data } = await client.post(`/study/${encodeURIComponent(uid)}/annotations`, payload);
  return data;
}
export async function deleteAnnotation(uid, id) {
  await client.delete(`/study/${encodeURIComponent(uid)}/annotations?id=${encodeURIComponent(id)}`);
}

export async function listKeyImages(uid) {
  const { data } = await client.get(`/study/${encodeURIComponent(uid)}/key-images`);
  return data.keyImages || [];
}
export async function toggleKeyImage(uid, image_index, note = null) {
  const { data } = await client.post(`/study/${encodeURIComponent(uid)}/key-images`, { image_index, note });
  return data;
}
export async function removeKeyImage(uid, image_index) {
  await client.delete(`/study/${encodeURIComponent(uid)}/key-images?image_index=${image_index}`);
}

export async function getPreferences() {
  const { data } = await client.get('/me/preferences');
  return data || {};
}
export async function savePreferences(prefs) {
  await client.put('/me/preferences', prefs);
}

export async function transcribeAudio(blob) {
  const { data } = await client.post('/transcribe', blob, {
    headers: { 'Content-Type': blob.type || 'audio/webm' },
  });
  return data?.text || '';
}

// Returns the URL string; caller window.open()s or sets an iframe src so the
// browser handles the download stream + filename.
export function studyZipUrl(uid) {
  const base = client.defaults.baseURL || '';
  return `${base}/study/${encodeURIComponent(uid)}/zip`;
}

// Download the ZIP via fetch with the MSAL token (click-to-download button).
export async function downloadStudyZip(uid, filenameHint = null) {
  const resp = await client.get(`/study/${encodeURIComponent(uid)}/zip`, { responseType: 'blob' });
  const blob = resp.data;
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filenameHint || `lumen-study-${uid.slice(-12)}.zip`;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 2000);
}

export async function patientTimeline(patientId) {
  const { data } = await client.get(`/patient/${encodeURIComponent(patientId)}/timeline`);
  return data.studies || [];
}

export async function listOrderingProviders() {
  const { data } = await client.get('/ordering-providers');
  return data.providers || [];
}

export default client;
